import type { JSONSchemaType } from 'ajv'
import type { MeetingBusySlot, MeetingMember } from '../../meetingService'
import type { MeetingRecord } from '../../../types/meetings'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

type MeetingConciergeDraft = {
  title: string
  scheduledAt: string
  durationMinutes: number
  sourceProvider: string
  participantSearchTerms: string[]
}

export const MEETING_SCHEDULE_ACTION_ID = 'meeting.schedule'
export const MEETING_CANCEL_ACTION_ID = 'meeting.cancel'

type MeetingScheduleArgs = { prompt: string; timezone: string }
type MeetingCancelArgs = { targets: Array<{ id: string; title: string }> }

export type MeetingScheduleConfirmPayload = { kind: 'MEETING_SCHEDULE'; draft: unknown }
export type MeetingCancelConfirmPayload = { kind: 'MEETING_CANCEL'; meetingIds: string[] }

const meetingScheduleArgsSchema: JSONSchemaType<MeetingScheduleArgs> = {
  type: 'object',
  properties: { prompt: { type: 'string' }, timezone: { type: 'string' } },
  required: ['prompt', 'timezone'],
  additionalProperties: false,
}

const meetingCancelArgsSchema: JSONSchemaType<MeetingCancelArgs> = {
  type: 'object',
  properties: {
    targets: {
      type: 'array',
      items: {
        type: 'object',
        properties: { id: { type: 'string' }, title: { type: 'string' } },
        required: ['id', 'title'],
        additionalProperties: false,
      },
    },
  },
  required: ['targets'],
  additionalProperties: false,
}

const emptyConciergeInput = (timezone: string) => ({
  timezone: timezone || 'UTC',
  meetings: [] as MeetingRecord[],
  calendarBusySlots: [] as MeetingBusySlot[],
  members: [] as MeetingMember[],
})

const formatOutcomeMessage = (draft: MeetingConciergeDraft): string => {
  const when = new Date(draft.scheduledAt)
  const whenLabel = when.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
  const parts = [
    `Draft ready: "${draft.title}" via ${draft.sourceProvider.replace('_', ' ').toLowerCase()}, ${whenLabel} for ${draft.durationMinutes} min.`,
  ]
  if (draft.participantSearchTerms.length > 0) parts.push(`Participants: ${draft.participantSearchTerms.join(', ')}.`)
  parts.push('Confirm to schedule it, or reply with changes.')
  return parts.join(' ')
}

const meetingScheduleAction: ActionDefinition<MeetingScheduleArgs> = {
  id: MEETING_SCHEDULE_ACTION_ID,
  label: 'Schedule meeting',
  description: 'Prepares a meeting concierge draft for explicit confirmation.',
  risk: 'MEDIUM',
  domain: 'meeting',
  surfaces: ['ai', 'catalog'],
  argsSchema: meetingScheduleArgsSchema,
  permission: (context) => context.canPerform('meetings.schedule'),
  run: async ({ prompt, timezone }, context): Promise<ActionOutcome> => {
    try {
      const { parseMeetingConciergeRequest } = await import('../../meetingConciergeService')
      const draft = parseMeetingConciergeRequest(prompt, emptyConciergeInput(timezone))
      const confirmPayload: MeetingScheduleConfirmPayload = { kind: 'MEETING_SCHEDULE', draft }
      return {
        kind: 'NEEDS_CONFIRMATION',
        message: formatOutcomeMessage(draft),
        confirmPayload,
        correlationId: context.correlationId!,
      }
    } catch (error) {
      return {
        kind: 'FAILED',
        message: error instanceof Error ? error.message : 'I could not parse that meeting request.',
        recoverable: true,
        correlationId: context.correlationId!,
      }
    }
  },
}

const meetingCancelAction: ActionDefinition<MeetingCancelArgs> = {
  id: MEETING_CANCEL_ACTION_ID,
  label: 'Cancel meeting',
  description: 'Prepares selected meetings for explicit cancellation confirmation.',
  risk: 'HIGH',
  domain: 'meeting',
  surfaces: ['ai', 'catalog'],
  argsSchema: meetingCancelArgsSchema,
  permission: (context) => context.canPerform('meetings.manage'),
  run: async ({ targets }, context): Promise<ActionOutcome> => {
    if (targets.length === 0) {
      return { kind: 'FAILED', message: 'Select the meeting you want to cancel and try again.', recoverable: true, correlationId: context.correlationId! }
    }
    const boundedTargets = targets.slice(0, 3)
    const confirmPayload: MeetingCancelConfirmPayload = {
      kind: 'MEETING_CANCEL',
      meetingIds: boundedTargets.map((target) => target.id),
    }
    return {
      kind: 'NEEDS_CONFIRMATION',
      message: `Cancel:\n${boundedTargets.map((target) => `• ${target.title}`).join('\n')}\n\nConfirm?`,
      confirmPayload,
      correlationId: context.correlationId!,
    }
  },
}

export const MEETING_RECORD_OPEN_ACTION_ID = 'meeting.record.open'
export const MEETING_RECORD_DUPLICATE_ACTION_ID = 'meeting.record.duplicate'
export const MEETING_RECORD_RESCHEDULE_ACTION_ID = 'meeting.record.reschedule'
export const MEETING_RECORD_DELETE_ACTION_ID = 'meeting.record.delete'

export const MEETING_ATTENDEE_PING_ACTION_ID = 'meeting.attendee.ping'
export const MEETING_ATTENDEE_ASSIGN_ACTION_ID = 'meeting.attendee.assign'
export const MEETING_ATTENDEE_REMOVE_ACTION_ID = 'meeting.attendee.remove'

export const MEETING_AGENDA_EDIT_ACTION_ID = 'meeting.agenda.edit'
export const MEETING_AGENDA_CONVERT_ACTION_ID = 'meeting.agenda.convert-to-task'
export const MEETING_AGENDA_DELETE_ACTION_ID = 'meeting.agenda.delete'

export const MEETING_ACTION_COMPLETE_ACTION_ID = 'meeting.action.complete'
export const MEETING_ACTION_ASSIGN_ACTION_ID = 'meeting.action.assign'
export const MEETING_ACTION_DELETE_ACTION_ID = 'meeting.action.delete'

export const MEETING_CANVAS_NEW_MEETING_ACTION_ID = 'meeting.canvas.new-meeting'
export const MEETING_CANVAS_NEW_AGENDA_ITEM_ACTION_ID = 'meeting.canvas.new-agenda-item'

export type MeetingOrbitTargetArgs = {
  id?: string
  title?: string
}

const meetingOrbitTargetArgsSchema: JSONSchemaType<MeetingOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  MEETING_RECORD_OPEN_ACTION_ID,
  MEETING_RECORD_DUPLICATE_ACTION_ID,
  MEETING_RECORD_RESCHEDULE_ACTION_ID,
  MEETING_RECORD_DELETE_ACTION_ID,
  MEETING_ATTENDEE_PING_ACTION_ID,
  MEETING_ATTENDEE_ASSIGN_ACTION_ID,
  MEETING_ATTENDEE_REMOVE_ACTION_ID,
  MEETING_AGENDA_EDIT_ACTION_ID,
  MEETING_AGENDA_CONVERT_ACTION_ID,
  MEETING_AGENDA_DELETE_ACTION_ID,
  MEETING_ACTION_COMPLETE_ACTION_ID,
  MEETING_ACTION_ASSIGN_ACTION_ID,
  MEETING_ACTION_DELETE_ACTION_ID,
])

const makeOrbitMeetingAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'meetings.view' | 'meetings.schedule' | 'meetings.manage',
): ActionDefinition<MeetingOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'meeting',
  surfaces: ['orbit', 'catalog'],
  argsSchema: meetingOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.meetingOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === MEETING_RECORD_OPEN_ACTION_ID) {
      handler = ops?.openMeeting ? () => ops.openMeeting!(args.id!) : undefined
    } else if (id === MEETING_RECORD_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateMeeting ? () => ops.duplicateMeeting!(args.id!) : undefined
    } else if (id === MEETING_RECORD_RESCHEDULE_ACTION_ID) {
      handler = ops?.rescheduleMeeting ? () => ops.rescheduleMeeting!(args.id!) : undefined
    } else if (id === MEETING_RECORD_DELETE_ACTION_ID) {
      handler = ops?.deleteMeeting ? () => ops.deleteMeeting!(args.id!) : undefined
    } else if (id === MEETING_ATTENDEE_PING_ACTION_ID) {
      handler = ops?.pingAttendee ? () => ops.pingAttendee!(args.id!) : undefined
    } else if (id === MEETING_ATTENDEE_ASSIGN_ACTION_ID) {
      handler = ops?.assignAttendee ? () => ops.assignAttendee!(args.id!) : undefined
    } else if (id === MEETING_ATTENDEE_REMOVE_ACTION_ID) {
      handler = ops?.removeAttendee ? () => ops.removeAttendee!(args.id!) : undefined
    } else if (id === MEETING_AGENDA_EDIT_ACTION_ID) {
      handler = ops?.editAgendaItem ? () => ops.editAgendaItem!(args.id!) : undefined
    } else if (id === MEETING_AGENDA_CONVERT_ACTION_ID) {
      handler = ops?.convertAgendaToTask ? () => ops.convertAgendaToTask!(args.id!) : undefined
    } else if (id === MEETING_AGENDA_DELETE_ACTION_ID) {
      handler = ops?.deleteAgendaItem ? () => ops.deleteAgendaItem!(args.id!) : undefined
    } else if (id === MEETING_ACTION_COMPLETE_ACTION_ID) {
      handler = ops?.completeActionItem ? () => ops.completeActionItem!(args.id!) : undefined
    } else if (id === MEETING_ACTION_ASSIGN_ACTION_ID) {
      handler = ops?.assignActionItem ? () => ops.assignActionItem!(args.id!) : undefined
    } else if (id === MEETING_ACTION_DELETE_ACTION_ID) {
      handler = ops?.deleteActionItem ? () => ops.deleteActionItem!(args.id!) : undefined
    } else if (id === MEETING_CANVAS_NEW_MEETING_ACTION_ID) {
      handler = ops?.createMeeting ? () => ops.createMeeting!() : undefined
    } else if (id === MEETING_CANVAS_NEW_AGENDA_ITEM_ACTION_ID) {
      handler = ops?.createAgendaItem ? () => ops.createAgendaItem!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: label,
      correlationId,
    }
  },
})

const meetingRecordOpenAction = makeOrbitMeetingAction(
  MEETING_RECORD_OPEN_ACTION_ID,
  'Open',
  'Opens meeting details and agenda.',
  'READ_ONLY',
  'meetings.view',
)

const meetingRecordDuplicateAction = makeOrbitMeetingAction(
  MEETING_RECORD_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Creates a duplicate copy of this meeting.',
  'LOW',
  'meetings.schedule',
)

const meetingRecordRescheduleAction = makeOrbitMeetingAction(
  MEETING_RECORD_RESCHEDULE_ACTION_ID,
  'Reschedule',
  'Reschedules the meeting date and time.',
  'LOW',
  'meetings.manage',
)

const meetingRecordDeleteAction = makeOrbitMeetingAction(
  MEETING_RECORD_DELETE_ACTION_ID,
  'Delete',
  'Deletes the meeting from workspace calendar.',
  'MEDIUM',
  'meetings.manage',
)

const meetingAttendeePingAction = makeOrbitMeetingAction(
  MEETING_ATTENDEE_PING_ACTION_ID,
  'Ping',
  'Sends a notification ping to the attendee.',
  'LOW',
  'meetings.view',
)

const meetingAttendeeAssignAction = makeOrbitMeetingAction(
  MEETING_ATTENDEE_ASSIGN_ACTION_ID,
  'Assign',
  'Assigns an attendee to agenda or action items.',
  'LOW',
  'meetings.manage',
)

const meetingAttendeeRemoveAction = makeOrbitMeetingAction(
  MEETING_ATTENDEE_REMOVE_ACTION_ID,
  'Remove',
  'Removes the attendee from the meeting roster.',
  'LOW',
  'meetings.manage',
)

const meetingAgendaEditAction = makeOrbitMeetingAction(
  MEETING_AGENDA_EDIT_ACTION_ID,
  'Edit',
  'Edits the title and description of the agenda item.',
  'LOW',
  'meetings.manage',
)

const meetingAgendaConvertAction = makeOrbitMeetingAction(
  MEETING_AGENDA_CONVERT_ACTION_ID,
  'Convert to task',
  'Converts the agenda item into an actionable task.',
  'LOW',
  'meetings.manage',
)

const meetingAgendaDeleteAction = makeOrbitMeetingAction(
  MEETING_AGENDA_DELETE_ACTION_ID,
  'Delete',
  'Deletes the agenda item.',
  'MEDIUM',
  'meetings.manage',
)

const meetingActionCompleteAction = makeOrbitMeetingAction(
  MEETING_ACTION_COMPLETE_ACTION_ID,
  'Complete',
  'Marks the action item as completed.',
  'LOW',
  'meetings.manage',
)

const meetingActionAssignAction = makeOrbitMeetingAction(
  MEETING_ACTION_ASSIGN_ACTION_ID,
  'Assign',
  'Assigns the action item to a workspace member.',
  'LOW',
  'meetings.manage',
)

const meetingActionDeleteAction = makeOrbitMeetingAction(
  MEETING_ACTION_DELETE_ACTION_ID,
  'Delete',
  'Deletes the action item.',
  'MEDIUM',
  'meetings.manage',
)

const meetingCanvasNewMeetingAction = makeOrbitMeetingAction(
  MEETING_CANVAS_NEW_MEETING_ACTION_ID,
  'New meeting',
  'Opens meeting creation modal or starts new draft.',
  'LOW',
  'meetings.schedule',
)

const meetingCanvasNewAgendaItemAction = makeOrbitMeetingAction(
  MEETING_CANVAS_NEW_AGENDA_ITEM_ACTION_ID,
  'New agenda item',
  'Adds a new agenda item to the active meeting.',
  'LOW',
  'meetings.manage',
)

export const registerMeetingActions = (): void => {
  if (!getAction(MEETING_SCHEDULE_ACTION_ID)) registerAction(meetingScheduleAction)
  if (!getAction(MEETING_CANCEL_ACTION_ID)) registerAction(meetingCancelAction)
  if (!getAction(MEETING_RECORD_OPEN_ACTION_ID)) registerAction(meetingRecordOpenAction)
  if (!getAction(MEETING_RECORD_DUPLICATE_ACTION_ID)) registerAction(meetingRecordDuplicateAction)
  if (!getAction(MEETING_RECORD_RESCHEDULE_ACTION_ID)) registerAction(meetingRecordRescheduleAction)
  if (!getAction(MEETING_RECORD_DELETE_ACTION_ID)) registerAction(meetingRecordDeleteAction)
  if (!getAction(MEETING_ATTENDEE_PING_ACTION_ID)) registerAction(meetingAttendeePingAction)
  if (!getAction(MEETING_ATTENDEE_ASSIGN_ACTION_ID)) registerAction(meetingAttendeeAssignAction)
  if (!getAction(MEETING_ATTENDEE_REMOVE_ACTION_ID)) registerAction(meetingAttendeeRemoveAction)
  if (!getAction(MEETING_AGENDA_EDIT_ACTION_ID)) registerAction(meetingAgendaEditAction)
  if (!getAction(MEETING_AGENDA_CONVERT_ACTION_ID)) registerAction(meetingAgendaConvertAction)
  if (!getAction(MEETING_AGENDA_DELETE_ACTION_ID)) registerAction(meetingAgendaDeleteAction)
  if (!getAction(MEETING_ACTION_COMPLETE_ACTION_ID)) registerAction(meetingActionCompleteAction)
  if (!getAction(MEETING_ACTION_ASSIGN_ACTION_ID)) registerAction(meetingActionAssignAction)
  if (!getAction(MEETING_ACTION_DELETE_ACTION_ID)) registerAction(meetingActionDeleteAction)
  if (!getAction(MEETING_CANVAS_NEW_MEETING_ACTION_ID)) registerAction(meetingCanvasNewMeetingAction)
  if (!getAction(MEETING_CANVAS_NEW_AGENDA_ITEM_ACTION_ID)) registerAction(meetingCanvasNewAgendaItemAction)
}

registerMeetingActions()
